import axios from "axios";
import Cookies from "js-cookie";
import { handleEditFormSubmit } from "../../../components/modals/EditableTable";
import { prepareZoneUpdatePayload } from "./zoneSettingsWizard";

const COPIE_SUFFIX_REGEX = / Copie (\d+)$/;

function getPositioningBase(positioning) {
  const trimmed = String(positioning ?? "").trim();
  const match = trimmed.match(COPIE_SUFFIX_REGEX);
  return match ? trimmed.slice(0, -match[0].length).trim() : trimmed;
}

/** Prochain libellé « … Copie N » en tenant compte des zones déjà listées. */
function nextDuplicatePositioning(
  sourcePositioning,
  existingPositionings = [],
) {
  const base = getPositioningBase(sourcePositioning);
  const prefix = base ? `${base} ` : "";

  let maxN = 0;
  for (const raw of existingPositionings) {
    const label = String(raw ?? "").trim();
    if (!label) continue;
    if (base && label === base) continue;
    const match = label.match(
      base
        ? new RegExp(`^${escapeRegExp(base)} Copie (\\d+)$`)
        : /^Copie (\d+)$/,
    );
    if (match) {
      maxN = Math.max(maxN, Number.parseInt(match[1], 10));
    }
  }

  return `${prefix}Copie ${maxN + 1}`.trim();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveObjectId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
}

function buildZoneDuplicateUpdatePayload(source, newId, positioning) {
  const contentCategory = resolveObjectId(
    source.contentCategory || source.wasteId,
  );

  return prepareZoneUpdatePayload({
    _id: newId,
    addressId: resolveObjectId(source.addressId),
    addressDestinationId: resolveObjectId(source.addressDestinationId),
    contract_id: resolveObjectId(source.contract_id),
    contentCategory,
    positioning,
    type: source.type || "unknown",
    volume: Number(source.volume) || 0,
    volumeUnit: source.volumeUnit || "m3",
    dimensions: source.dimensions,
    fullWeight: source.fullWeight,
    weightUnit: source.weightUnit,
    limitFillingLevel: source.limitFillingLevel ?? 80,
    packmatActive: !!source.packmatActive,
    alertForFilling: !!source.alertForFilling,
    alertForAnomaly: !!source.alertForAnomaly,
    status: source.status || "drop_driver",
    showDepotCycle: !!source.showDepotCycle,
    activatedFillingLevelOption: !!source.activatedFillingLevelOption,
    activatedSortingErrorOption: !!source.activatedSortingErrorOption,
    activatedActivityOption: !!source.activatedActivityOption,
    characterization: !!source.characterization,
    qualicheck: !!source.qualicheck,
    alertLight: source.alertLight,
  });
}

const LIST_QUERY_PARAMS = {
  page: null,
  limit: null,
  fieldSort: "addressName",
  orderSort: "1",
  keywordFilter: JSON.stringify([[], [], [], [], [], []]),
  arrayFilterFillingLevel: JSON.stringify([0, 100]),
  typeFillingFilter: "all",
  stopTime: "",
};

export async function duplicateContainerZone({
  source,
  existingContainers = [],
}) {
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");
  const addressId = resolveObjectId(source.addressId);

  if (!addressId) {
    throw new Error("missing_address");
  }

  const positioning = nextDuplicatePositioning(
    source.positioning,
    existingContainers.map((c) => c.positioning),
  );

  const addResponse = await axios.post(
    `${API}container/add`,
    { address: addressId },
    { headers: { authorization: `Bearer ${token}` } },
  );

  const created = addResponse?.data?.newContainerMongoDB;
  if (!created?._id) {
    throw new Error("create_failed");
  }

  const updatePayload = buildZoneDuplicateUpdatePayload(
    source,
    created._id,
    positioning,
  );

  const updateResponse = await handleEditFormSubmit(updatePayload, "container");
  if (updateResponse?.status !== 200) {
    throw new Error("update_failed");
  }

  return {
    createdId: created._id,
    positioning,
    saved: updateResponse?.data?.data,
  };
}

export { LIST_QUERY_PARAMS };
